'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Send, Loader2, ChevronDown, Film, Camera, Sparkles, Scissors } from 'lucide-react';

interface ContactForm {
  name: string;
  company: string;
  email: string;
  service: string;
  budget: string;
  message: string;
}

const serviceOptions = [
  { value: 'video', label: 'Video Production', icon: Film },
  { value: 'onsite', label: 'On-Site Shooting', icon: Camera },
  { value: 'ai', label: 'AI-Enhanced Projects', icon: Sparkles },
  { value: 'editing', label: 'Post-Production & Editing', icon: Scissors },
  { value: 'multiple', label: 'Multiple Services', icon: null },
];

const budgetOptions = [
  { value: 'under10k', label: 'Under $10,000' },
  { value: '10k-25k', label: '$10,000 – $25,000' },
  { value: '25k-50k', label: '$25,000 – $50,000' },
  { value: '50k-100k', label: '$50,000 – $100,000' },
  { value: 'over100k', label: '$100,000+' },
  { value: 'undisclosed', label: 'Prefer not to say' },
];

export default function ContactCTA() {
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ContactForm>();

  // Backend: POST /api/contact — replace with actual API call
  const onSubmit = async (data: ContactForm) => {
    setLoading(true);
    await new Promise((r) => setTimeout(r, 1600));
    setLoading(false);
    setSent(true);
    reset();
    toast.success('Your brief has been received.', {
      description: 'A member of our team will be in touch within 24 hours.',
      duration: 5000,
    });
  };

  return (
    <section id="contact" className="py-24 xl:py-32 scroll-mt-20 relative overflow-hidden" aria-label="Contact and project inquiry">
      {/* Background */}
      <div className="absolute inset-0 bg-studio-950" />
      <div className="absolute inset-0 bg-hero-glow opacity-60" />
      <div
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `linear-gradient(rgba(201,168,76,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(201,168,76,0.8) 1px, transparent 1px)`,
          backgroundSize: '60px 60px',
        }}
      />

      <div className="relative max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-12 2xl:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 xl:gap-20 items-start">
          {/* Left: copy */}
          <div>
            <p className="text-gold-400 text-xs font-semibold tracking-[0.2em] uppercase mb-4">
              Start a Project
            </p>
            <h2 className="font-display font-bold text-4xl md:text-5xl xl:text-6xl text-white leading-tight mb-6">
              Let's Build
              <br />
              <span className="gradient-text-gold">Something Real.</span>
            </h2>
            <p className="text-studio-400 text-base leading-relaxed mb-10 max-w-lg">
              Tell us what you're working on. We'll come back with a clear proposal — no fluff, no overpriced pitch decks. Just a straightforward plan for making great work together.
            </p>

            {/* Contact details */}
            <div className="space-y-4">
              {[
                { label: 'New Business', value: 'hello@nasarc.studio' },
                { label: 'Existing Clients', value: 'clients@nasarc.studio' },
                { label: 'Location', value: 'Los Angeles · London · Dubai' },
                { label: 'Response Time', value: 'Within 24 hours on weekdays' },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-4">
                  <span className="text-studio-500 text-xs font-medium w-32 shrink-0">{item.label}</span>
                  <span className="text-studio-200 text-sm">{item.value}</span>
                </div>
              ))}
            </div>

            {/* Tier note */}
            <div className="mt-10 p-4 bg-gold-500/8 border border-gold-500/20 rounded-xl">
              <p className="text-gold-300 text-xs font-medium mb-1">Already a Gold or VIP client?</p>
              <p className="text-studio-400 text-xs leading-relaxed">
                Use your dedicated Slack channel or reach your account manager directly. This form is for new inquiries only.
              </p>
            </div>
          </div>

          {/* Right: form */}
          <div className="bg-studio-900 rounded-2xl border border-studio-700/50 p-6 xl:p-8">
            {sent ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 rounded-full bg-gold-500/15 border border-gold-500/30 flex items-center justify-center mb-5">
                  <Send size={24} className="text-gold-400" />
                </div>
                <h3 className="font-display font-bold text-xl text-white mb-2">Brief Received</h3>
                <p className="text-studio-400 text-sm leading-relaxed max-w-xs mb-6">
                  We'll review your project details and get back to you within 24 hours with initial thoughts.
                </p>
                <button
                  onClick={() => setSent(false)}
                  className="text-gold-400 text-sm font-medium hover:text-gold-300 transition-colors"
                >
                  Submit another inquiry
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <h3 className="font-display font-semibold text-lg text-white mb-6">Project Brief</h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  {/* Name */}
                  <div>
                    <label htmlFor="name" className="block text-studio-300 text-xs font-medium mb-1.5">
                      Your Name <span className="text-gold-500">*</span>
                    </label>
                    <input
                      id="name"
                      type="text"
                      placeholder="Amara Osei"
                      {...register('name', { required: 'Name is required' })}
                      className={`w-full px-3.5 py-2.5 bg-studio-950/60 border rounded-xl text-white text-sm placeholder-studio-600 focus:outline-none focus:ring-2 focus:ring-gold-500/40 transition-all duration-200 ${
                        errors.name ? 'border-red-500/60' : 'border-studio-700/50 focus:border-gold-500/40'
                      }`}
                    />
                    {errors.name && (
                      <p className="mt-1 text-red-400 text-xs">{errors.name.message}</p>
                    )}
                  </div>

                  {/* Company */}
                  <div>
                    <label htmlFor="company" className="block text-studio-300 text-xs font-medium mb-1.5">
                      Company / Brand <span className="text-gold-500">*</span>
                    </label>
                    <input
                      id="company"
                      type="text"
                      placeholder="Meridian Capital"
                      {...register('company', { required: 'Company is required' })}
                      className={`w-full px-3.5 py-2.5 bg-studio-950/60 border rounded-xl text-white text-sm placeholder-studio-600 focus:outline-none focus:ring-2 focus:ring-gold-500/40 transition-all duration-200 ${
                        errors.company ? 'border-red-500/60' : 'border-studio-700/50 focus:border-gold-500/40'
                      }`}
                    />
                    {errors.company && (
                      <p className="mt-1 text-red-400 text-xs">{errors.company.message}</p>
                    )}
                  </div>
                </div>

                {/* Email */}
                <div className="mb-4">
                  <label htmlFor="email" className="block text-studio-300 text-xs font-medium mb-1.5">
                    Work Email <span className="text-gold-500">*</span>
                  </label>
                  <input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email address' },
                    })}
                    className={`w-full px-3.5 py-2.5 bg-studio-950/60 border rounded-xl text-white text-sm placeholder-studio-600 focus:outline-none focus:ring-2 focus:ring-gold-500/40 transition-all duration-200 ${
                      errors.email ? 'border-red-500/60' : 'border-studio-700/50 focus:border-gold-500/40'
                    }`}
                  />
                  {errors.email && (
                    <p className="mt-1 text-red-400 text-xs">{errors.email.message}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  {/* Service */}
                  <div>
                    <label htmlFor="service" className="block text-studio-300 text-xs font-medium mb-1.5">
                      Service Needed <span className="text-gold-500">*</span>
                    </label>
                    <div className="relative">
                      <select
                        id="service"
                        {...register('service', { required: 'Select a service' })}
                        className={`w-full px-3.5 py-2.5 bg-studio-950/60 border rounded-xl text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-gold-500/40 transition-all duration-200 ${
                          errors.service ? 'border-red-500/60 text-red-400' : 'border-studio-700/50 text-white focus:border-gold-500/40'
                        }`}
                      >
                        <option value="" className="bg-studio-900 text-studio-400">Select service…</option>
                        {serviceOptions.map((opt) => (
                          <option key={opt.value} value={opt.value} className="bg-studio-900 text-white">
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-studio-500 pointer-events-none" />
                    </div>
                    {errors.service && (
                      <p className="mt-1 text-red-400 text-xs">{errors.service.message}</p>
                    )}
                  </div>

                  {/* Budget */}
                  <div>
                    <label htmlFor="budget" className="block text-studio-300 text-xs font-medium mb-1.5">
                      Estimated Budget
                    </label>
                    <div className="relative">
                      <select
                        id="budget"
                        {...register('budget')}
                        className="w-full px-3.5 py-2.5 bg-studio-950/60 border border-studio-700/50 rounded-xl text-white text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-gold-500/40 focus:border-gold-500/40 transition-all duration-200"
                      >
                        <option value="" className="bg-studio-900 text-studio-400">Select range…</option>
                        {budgetOptions.map((opt) => (
                          <option key={opt.value} value={opt.value} className="bg-studio-900 text-white">
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-studio-500 pointer-events-none" />
                    </div>
                  </div>
                </div>

                {/* Message */}
                <div className="mb-6">
                  <label htmlFor="message" className="block text-studio-300 text-xs font-medium mb-1.5">
                    Project Brief <span className="text-gold-500">*</span>
                  </label>
                  <p className="text-studio-600 text-[11px] mb-1.5">
                    Tell us what you're trying to make, who it's for, and when you need it.
                  </p>
                  <textarea
                    id="message"
                    rows={4}
                    placeholder="We're launching a new product in Q3 and need a 60-second hero video for the campaign…"
                    {...register('message', {
                      required: 'A brief description is required',
                      minLength: { value: 30, message: 'Please provide at least 30 characters' },
                    })}
                    className={`w-full px-3.5 py-2.5 bg-studio-950/60 border rounded-xl text-white text-sm placeholder-studio-600 focus:outline-none focus:ring-2 focus:ring-gold-500/40 transition-all duration-200 resize-none scrollbar-thin ${
                      errors.message ? 'border-red-500/60' : 'border-studio-700/50 focus:border-gold-500/40'
                    }`}
                  />
                  {errors.message && (
                    <p className="mt-1 text-red-400 text-xs">{errors.message.message}</p>
                  )}
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2.5 py-3.5 gradient-gold text-studio-950 font-semibold text-sm rounded-xl hover:opacity-90 disabled:opacity-70 disabled:cursor-not-allowed transition-all duration-200 active:scale-[0.98]"
                  style={{ minHeight: '48px' }}
                >
                  {loading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Sending Brief…
                    </>
                  ) : (
                    <>
                      Send Project Brief
                      <Send size={14} />
                    </>
                  )}
                </button>

                <p className="mt-3 text-studio-600 text-[11px] text-center">
                  By submitting, you agree to NASARC's{' '}
                  <button type="button" className="text-studio-500 hover:text-studio-300 underline underline-offset-2 transition-colors">
                    Privacy Policy
                  </button>
                  . We'll never share your details.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}